import ApolloClient from '@/packages/apollo-client'
import SentryClient from '@/packages/sentry'
import { FETCH_PICTURES, UPDATE_PICTURES, UPLOAD_PICTURES, DELETE_PICTURES, CONFIRM_PART_PICTURES_UPLOAD } from './action-types'
import * as PartMutationTypes from '../../mutation-types'
import * as PartActionTypes from '../../action-types'

import FetchPartPictures from './graphql/FetchPartPictures.gql'
import GetPartPicturePresignedUrls from './graphql/GetPartPicturePresignedUrls.gql'
import EditPartPictures from './graphql/EditPartPictures.gql'
import DeletePartPictures from './graphql/DeletePartPictures.gql'
import ConfirmPartPicturesUpload from './graphql/ConfirmPartPicturesUpload.gql'
import { BasePicturesModel } from '@/models/BasePicturesModel'

export default {
	actions: {
		async [FETCH_PICTURES] ({ commit, rootState }, { partId }) {
			const currentPart = rootState.Parts.currentPart

			if (currentPart.id === partId) {
				commit(PartMutationTypes.SET_LOADING, { partPhotos: true })
			}

			const { data } = await ApolloClient.query({
				query: FetchPartPictures,
				variables: {
					id: partId
				}
			})

			if (currentPart.id === partId) {
				commit(PartMutationTypes.SET_LOADING, { partPhotos: false })
				commit(PartMutationTypes.EDIT_PART, {
					pictures: data.part.pictures
				})
			}
			const picturesModel = new BasePicturesModel(data.part)
			return picturesModel.picturesV2
		},

		async [UPLOAD_PICTURES] ({}, { departmentId, resourceId, pictures, skipUpload = false, processAi = true }) {
			return await SentryClient.withReplay(async () => {
				SentryClient.addGraphQLBreadcrumb('GetPartPicturePresignedUrls', {
					departmentId,
					resourceId,
					pictureCount: pictures.length,
					skipUpload,
					processAi
				})

				const { data: { getPartPicturePresignedUrls } } = await ApolloClient.mutate({
					mutation: GetPartPicturePresignedUrls,
					variables: {
						payload: {
							departmentId,
							resourceId,
							processAi,
							pictures: pictures.map(picture => ({
								order: picture.order,
								extension: picture.extension,
								visibility: picture.visibility
							}))
						}
					}
				})

				SentryClient.Sentry.addBreadcrumb({
					category: 'api',
					message: 'Received presigned URLs',
					level: 'info',
					data: {
						urlCount: getPartPicturePresignedUrls.pictures.length
					}
				})

				if (skipUpload) {
					SentryClient.Sentry.addBreadcrumb({
						category: 'upload',
						message: 'Collecting presigned URLs',
						level: 'info'
					})

					return getPartPicturePresignedUrls.pictures.map(record => {
						const picture  = pictures.find(picture => parseInt(picture.order, 10) === parseInt(record.order, 10))
						return {
							...picture,
							...record
						}
					})
				} else {
					SentryClient.Sentry.addBreadcrumb({
						category: 'upload',
						message: 'Starting file uploads to S3',
						level: 'info',
						data: {
							fileCount: pictures.length
						}
					})

					const uploads = getPartPicturePresignedUrls.pictures.map(presigned => {
						const picture  = pictures.find(picture => parseInt(picture.order, 10) === parseInt(presigned.order, 10))
						return fetch(presigned.url, { method: 'PUT', body: picture.file, headers: { 'Content-Type': presigned.contentType || picture.file?.type } })
					})

					await Promise.all(uploads)

					SentryClient.Sentry.addBreadcrumb({
						category: 'upload',
						message: 'All files uploaded successfully to S3',
						level: 'info',
						data: {
							fileCount: uploads.length
						}
					})

					return getPartPicturePresignedUrls.pictures.map(picture => {
						delete picture.url
						delete picture.__typename
						return picture
					})
				}
			}, {
				operation: 'uploadPictures',
				departmentId,
				resourceId,
				pictureCount: pictures.length,
				skipUpload
			})
		},

		async [UPDATE_PICTURES] ({}, { resourceId, pictures }) {
			await ApolloClient.mutate({
				mutation: EditPartPictures,
				variables: {
					payload: {
						resourceId,
						pictures
					}
				}
			})
		},

		async [DELETE_PICTURES] ({}, { resourceId, ids }) {
			await ApolloClient.mutate({
				mutation: DeletePartPictures,
				variables: {
					payload: {
						resourceId,
						ids
					}
				}
			})
		},

		async [CONFIRM_PART_PICTURES_UPLOAD] ({ dispatch, rootState }, payload) {
			const { resourceId, pictureIds, processAi, sessionId, requestId } = payload
			const currentPart = rootState.Parts.currentPart

			return await SentryClient.withReplay(async () => {
				SentryClient.addGraphQLBreadcrumb('ConfirmPartPicturesUpload', {
					resourceId,
					pictureIdCount: pictureIds.length,
					requestId
				})

				await ApolloClient.mutate({
					mutation: ConfirmPartPicturesUpload,
					variables: {
						payload: {
							resourceId,
							pictureIds,
							processAi,
							sessionId,
							requestId
						}
					}
				})

				SentryClient.Sentry.addBreadcrumb({
					category: 'api',
					message: 'Pictures confirmed successfully',
					level: 'info',
					data: {
						confirmedCount: pictureIds.length
					}
				})

				if (resourceId && currentPart.id === resourceId) {
					SentryClient.Sentry.addBreadcrumb({
						category: 'state',
						message: 'Refreshing part tasks and locations',
						level: 'info',
						data: {
							partId: resourceId
						}
					})

					dispatch(PartActionTypes.FETCH_PART_TASKS, resourceId)
					dispatch(PartActionTypes.FETCH_PART_LOCATIONS, resourceId)
				}
			}, {
				operation: 'confirmPartPicturesUpload',
				resourceId,
				pictureIds,
				pictureIdCount: pictureIds.length,
				requestId
			})
		}
	}
}
